/**
 * Source registry for the India BRSR Readiness Checker.
 * Last reviewed 13 October 2026. Confirm the SEBI circular listing before relying on this file as current law.
 */

export const REGULATORY_LAST_REVIEWED = '2026-10-13'

export type SourceStatus = 'confirmed' | 'requires_review'

export interface RegulatorySource {
  id: string
  title: string
  authority: string
  url: string
  documentDate: string
  circularNumber?: string
  effectiveOrReportingPeriod: string
  lastReviewed: string
  status: SourceStatus
  notes: string
}

export interface NgrbcPrinciple {
  id: string
  title: string
  statement: string
  sourceId: string
}

export const REGULATORY_SOURCES: RegulatorySource[] = [
  {
    id: 'sebi-2021-brsr',
    title: 'Business Responsibility and Sustainability Reporting by listed entities',
    authority: 'Securities and Exchange Board of India',
    url: 'https://www.sebi.gov.in/legal/circulars/may-2021/business-responsibility-and-sustainability-reporting-by-listed-entities_50096.html',
    documentDate: '2021-05-10',
    circularNumber: 'SEBI/HO/CFD/CMD-2/P/CIR/2021/562',
    effectiveOrReportingPeriod:
      'Mandatory BRSR for the top 1,000 listed entities by market capitalisation from FY 2022-23. The July 2023 circular cites this circular number and date.',
    lastReviewed: REGULATORY_LAST_REVIEWED,
    status: 'confirmed',
    notes:
      'The July 2023 BRSR Core circular expressly cites SEBI/HO/CFD/CMD-2/P/CIR/2021/562 dated 10 May 2021 as the circular that prescribed BRSR. This assessment uses that cohort rule. The full 2021 annexure was not re-parsed line by line in this review.',
  },
  {
    id: 'sebi-2023-core',
    title: 'BRSR Core – Framework for assurance and ESG disclosures for value chain',
    authority: 'Securities and Exchange Board of India',
    url: 'https://www.sebi.gov.in/legal/circulars/jul-2023/brsr-core-framework-for-assurance-and-esg-disclosures-for-value-chain_73854.html',
    documentDate: '2023-07-12',
    circularNumber: 'SEBI/HO/CFD/CFD-SEC-2/P/CIR/2023/122',
    effectiveOrReportingPeriod:
      'Updated BRSR format for the top 1,000 listed entities from FY 2023-24. BRSR Core assessment or assurance follows a separate glide path, restated in March 2025.',
    lastReviewed: REGULATORY_LAST_REVIEWED,
    status: 'confirmed',
    notes:
      'Introduced BRSR Core as a subset of BRSR KPIs and a value-chain disclosure framework. The July 2023 “reasonable assurance” wording and the original value-chain comply-or-explain dates were amended by the March 2025 circular. Do not apply the July 2023 value-chain timetable as current law. Attribute groupings in this tool are summarised from Annexure I of this circular.',
  },
  {
    id: 'sebi-2025-ease',
    title:
      'Measures to facilitate ease of doing business with respect to framework for assurance or assessment, ESG disclosures for value chain, and introduction of voluntary disclosure on green credits',
    authority: 'Securities and Exchange Board of India',
    url: 'https://www.sebi.gov.in/legal/circulars/mar-2025/measures-to-facilitate-ease-of-doing-business-with-respect-to-framework-for-assurance-or-assessment-esg-disclosures-for-value-chain-and-introduction-of-voluntary-disclosure-on-green-credits_93102.html',
    documentDate: '2025-03-28',
    circularNumber: 'SEBI/HO/CFD/CFD-PoD-1/P/CIR/2025/42',
    effectiveOrReportingPeriod:
      'Restates the BRSR Core glide path as mandatory assessment or assurance. Value-chain ESG disclosures are voluntary for the top 250 listed entities from FY 2025-26. Value-chain assessment or assurance is voluntary from FY 2026-27. Green-credit disclosure is a voluntary leadership indicator.',
    lastReviewed: REGULATORY_LAST_REVIEWED,
    status: 'confirmed',
    notes:
      'Read against the circular PDF published on sebi.gov.in (attachment dated March 2025). The circular also cites LODR amendments via Gazette ID CG-MH-E-28032025-262027 dated 28 March 2025. The gazette text itself was not separately downloaded. The numeric value-chain partner threshold was described as revised, but the replacement definition was not extracted in full, so this tool does not apply a 2% or 75% partner test.',
  },
  {
    id: 'sebi-circular-index',
    title: 'SEBI legal circulars listing',
    authority: 'Securities and Exchange Board of India',
    url: 'https://www.sebi.gov.in/sebiweb/home/HomeAction.do?doListing=yes&sid=1&ssid=7&smid=0',
    documentDate: '2026-10-13',
    effectiveOrReportingPeriod: 'Living index of SEBI circulars.',
    lastReviewed: REGULATORY_LAST_REVIEWED,
    status: 'requires_review',
    notes:
      'No BRSR applicability circular later than 28 March 2025 was identified in this review. Re-check this listing before treating the configuration as current law. This is an index, not a substantive rule.',
  },
  {
    id: 'ngrbc-pib',
    title: 'National Guidelines on Responsible Business Conduct',
    authority: 'Ministry of Corporate Affairs, published via Press Information Bureau',
    url: 'https://www.pib.gov.in/Pressreleaseshare.aspx?PRID=1568750',
    documentDate: '2019-03-13',
    effectiveOrReportingPeriod: 'Nine principles of responsible business conduct, which structure BRSR Section B and Section C.',
    lastReviewed: REGULATORY_LAST_REVIEWED,
    status: 'confirmed',
    notes:
      'Principle statements are taken from the PIB release of 13 March 2019. The historical MCA PDF URL (mca.gov.in/Ministry/pdf/NationalGuildeline_15032019.pdf) returned an error page on review. An IICA-hosted copy is available at https://iica.nic.in/images/National-Guidelines-on-Responsible%20Business%20Conduct.pdf and should be checked against the government text.',
  },
]

export const NGRBC_PRINCIPLES: NgrbcPrinciple[] = [
  {
    id: 'P1',
    title: 'Ethics, integrity and accountability',
    statement:
      'Businesses should conduct and govern themselves with integrity in a manner that is ethical, transparent and accountable.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P2',
    title: 'Sustainable and safe goods and services',
    statement: 'Businesses should provide goods and services in a manner that is sustainable and safe.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P3',
    title: 'Employee well-being',
    statement:
      'Businesses should respect and promote the well-being of all employees, including those in their value chains.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P4',
    title: 'Stakeholder responsiveness',
    statement: 'Businesses should respect the interests of and be responsive to all their stakeholders.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P5',
    title: 'Human rights',
    statement: 'Businesses should respect and promote human rights.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P6',
    title: 'Environmental protection',
    statement: 'Businesses should respect and make efforts to protect and restore the environment.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P7',
    title: 'Responsible public policy advocacy',
    statement:
      'Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P8',
    title: 'Inclusive growth and equitable development',
    statement: 'Businesses should promote inclusive growth and equitable development.',
    sourceId: 'ngrbc-pib',
  },
  {
    id: 'P9',
    title: 'Responsible consumer engagement',
    statement: 'Businesses should engage with and provide value to their consumers in a responsible manner.',
    sourceId: 'ngrbc-pib',
  },
]

export function sourceById(id: string): RegulatorySource | undefined {
  return REGULATORY_SOURCES.find((source) => source.id === id)
}
